import { hasValidChars } from '../utils';

// `null`/`undefined` deviennent '' (traités comme absents par les gardes de
// présence) ; toute autre valeur est coercée pour que `.length` porte bien sur
// la représentation textuelle et non sur `undefined`.
const normalizeToString = (value) =>
  value === null || value === undefined ? '' : String(value);

// Cas rencontré en production : `line1` peut porter le numéro de rue seul
// (ex. "37") et `line2` le nom de rue. Valider `address1` isolément sur un
// seuil de 3 caractères rejetait cette adresse pourtant parfaitement
// livrable, et le conseiller ne pouvait pas créer la SAV. On valide donc les
// deux lignes ensemble : c'est leur combinaison qui constitue l'adresse.
// Une adresse réellement vide ou réduite à un bruit de 1-2 caractères reste
// rejetée.
const MIN_ADDRESS_LENGTH = 3;

export const validateAddressLines = (address) => {
  if (!address) {
    return "L'adresse est requise";
  }

  const line1 = normalizeToString(address.address1).trim();
  const line2 = normalizeToString(address.address2).trim();

  if (!line1 && !line2) {
    return "L'adresse est requise";
  }

  // On somme les longueurs utiles au lieu de concaténer avec un séparateur :
  // un espace de jointure gonflerait la mesure ("3" + " " + "7" compterait 3
  // caractères alors que la saisie n'en porte que 2).
  const meaningfulLength = line1.length + line2.length;
  if (meaningfulLength < MIN_ADDRESS_LENGTH) {
    return `L'adresse doit contenir au moins ${MIN_ADDRESS_LENGTH} caractères (numéro et complément inclus)`;
  }

  return null;
};

// Libellés lisibles par le conseiller, alignés sur la forme de `newErrors`
// produite par `validateSavFields`. Colocalisés ici volontairement : toute
// nouvelle clé d'erreur doit être nommée au même endroit, sinon le snackbar
// resterait muet sur ce champ.
const TOP_LEVEL_ERROR_LABELS = {
  lastName: 'nom',
  firstName: 'prénom',
  phone: 'téléphone',
  reason: 'motif',
  orderPicker: 'préparateur',
  lines: 'produits',
  amountTtcNet: 'montant TTC',
};

const ADDRESS_ERROR_LABELS = {
  address1: 'adresse',
  address2: "complément d'adresse",
  postalCode: 'code postal',
  city: 'ville',
};

export const getSavErrorLabels = (errors) => {
  if (!errors) {
    return [];
  }

  const labels = [];

  for (const [key, label] of Object.entries(TOP_LEVEL_ERROR_LABELS)) {
    if (errors[key]) {
      labels.push(label);
    }
  }

  const addressGroups = [
    { key: 'shippingAddress', suffix: 'de livraison' },
    { key: 'billingAddress', suffix: 'de facturation' },
  ];

  for (const { key, suffix } of addressGroups) {
    const group = errors[key];
    if (!group) {
      continue;
    }
    for (const [field, label] of Object.entries(ADDRESS_ERROR_LABELS)) {
      if (group[field]) {
        labels.push(`${label} ${suffix}`);
      }
    }
  }

  return labels;
};

export const validateSavFields = (
  formData,
  setFormError,
  isErrorRequired = true
) => {
  let isValid = true;
  // shippingAddress/billingAddress sont toujours initialisés comme des objets :
  // SetAddress.jsx lit `formError.shippingAddress?.address1` (etc.), jamais
  // `formError.shippingAddress` comme chaîne. Ne jamais réassigner ces clés en
  // string, sous peine de "Cannot set properties of undefined" plus bas.
  const newErrors = {
    shippingAddress: {},
    billingAddress: {},
  };

  if (!formData?.lastName || formData.lastName?.length < 3) {
    newErrors.lastName = 'Le nom doit contenir au moins 3 caractères';
    isValid = false;
  }

  if (!formData?.firstName || formData.firstName?.length < 3) {
    newErrors.firstName = 'Le prénom doit contenir au moins 3 caractères';
    isValid = false;
  }

  if (!formData?.phone || formData.phone?.length < 10) {
    newErrors.phone =
      'Le numéro de téléphone doit contenir au moins 10 caractères';
    isValid = false;
  }

  if (!formData?.reason?.length) {
    newErrors.reason = 'Un motif doit être sélectionné';
    isValid = false;
  }

  if (!formData?.orderPicker?.length) {
    newErrors.orderPicker = 'Un préparateur doit être sélectionné';
    isValid = false;
  }

  // verification valide shipping address
  const shippingAddress = formData?.shippingAddress;
  const shippingAddressError = validateAddressLines(shippingAddress);
  if (shippingAddressError) {
    newErrors.shippingAddress.address1 = shippingAddressError;
    isValid = false;
  }

  // Retour CodeRabbit (3e tour) : `postalCode` peut remonter en nombre depuis
  // Mongo (ex. 75001). `(12).length` vaut `undefined`, donc `undefined < 3`
  // était `false` : la garde de longueur minimale était silencieusement
  // contournée et un code postal numérique à 2 chiffres passait, alors que la
  // chaîne '12' était bien rejetée. On normalise en chaîne avant de tester
  // présence et longueur, comme pour `hasValidChars` plus bas.
  const shippingPostalCode = normalizeToString(shippingAddress?.postalCode);
  if (!shippingPostalCode) {
    newErrors.shippingAddress.postalCode = 'Le code postal est requis';
    isValid = false;
  } else if (shippingPostalCode.length < 3) {
    newErrors.shippingAddress.postalCode =
      'Le code postal doit contenir au moins 3 caractères';
    isValid = false;
  }

  if (!shippingAddress?.city) {
    newErrors.shippingAddress.city = 'La ville est requise';
    isValid = false;
  } else if (shippingAddress.city?.length < 3) {
    newErrors.shippingAddress.city =
      'La ville doit contenir au moins 3 caractères';
    isValid = false;
  }

  // verification valide billing address
  const billingAddress = formData?.billingAddress;
  const billingAddressError = validateAddressLines(billingAddress);
  if (billingAddressError) {
    newErrors.billingAddress.address1 = billingAddressError;
    isValid = false;
  }

  const billingPostalCode = normalizeToString(billingAddress?.postalCode);
  if (!billingPostalCode) {
    newErrors.billingAddress.postalCode = 'Le code postal est requis';
    isValid = false;
  } else if (billingPostalCode.length < 3) {
    newErrors.billingAddress.postalCode =
      'Le code postal doit contenir au moins 3 caractères';
    isValid = false;
  }

  if (!billingAddress?.city) {
    newErrors.billingAddress.city = 'La ville est requise';
    isValid = false;
  } else if (billingAddress.city?.length < 3) {
    newErrors.billingAddress.city =
      'La ville doit contenir au moins 3 caractères';
    isValid = false;
  }

  if (!formData?.lines?.length) {
    newErrors.lines = "Aucun produit n'a été ajouté";
    isValid = false;
  } else {
    formData.lines.forEach((item) => {
      if (item?.internalProduct?.length != 10) {
        newErrors.lines =
          'Les références des produit doivent être au format: ABC-000000';
        isValid = false;
      }
    });
  }

  if (formData?.isPayable) {
    // Coerce avant de tester : `savDraft.amount` (backend) n'est pas
    // garanti numérique côté type, une chaîne "50" ne doit pas être rejetée
    // à tort. On rejette en revanche tout montant <= 0 (dont un négatif
    // hérité d'un brouillon backend via `savDraft.amount || 100`), pas
    // seulement `0`.
    const amount = Number(formData?.amountTtcNet);
    if (!Number.isFinite(amount) || amount <= 0) {
      newErrors.amountTtcNet =
        'Le montant TTC doit être supérieur à 0 si la commande est payante';
      isValid = false;
    }
  }

  // Validate special characters in address fields
  const shippingAddressFields = {
    firstName: formData?.firstName,
    lastName: formData?.lastName,
    address1: shippingAddress?.address1,
    address2: shippingAddress?.address2,
    postalCode: shippingPostalCode,
    city: shippingAddress?.city,
  };

  const billingAddressFields = {
    firstName: formData?.firstName,
    lastName: formData?.lastName,
    address1: billingAddress?.address1,
    address2: billingAddress?.address2,
    postalCode: billingPostalCode,
    city: billingAddress?.city,
  };

  // firstName/lastName restent au niveau supérieur de newErrors (cohérent
  // avec les lignes 18-26) ; address1/address2/postalCode/city doivent être
  // imbriqués sous shippingAddress/billingAddress car c'est ce que lit
  // SavAddress.jsx (formError[keyName]?.address1, etc.) — les écrire à plat
  // faisait que l'erreur n'était jamais affichée au conseiller.
  const addressErrorKeys = ['address1', 'address2', 'postalCode', 'city'];

  // Retour CodeRabbit (2e tour), point 1 : `shippingAddress.postalCode` /
  // `billingAddress.postalCode` peuvent être stockés en nombre côté Mongo
  // (ex. 75001) et remontent bruts depuis SetAddress/SavOrderForm (seul le
  // falsy est coercé en amont, cf. SavOrderForm.jsx `|| ''`). `hasValidChars`
  // (utils.js) fait `[...string]` puis `.normalize()`, qui lèvent sur une
  // valeur non-string. On coerce ici avec `String(value)` plutôt que de
  // rejeter la valeur : un code postal numérique est une donnée légitime mal
  // typée, pas une saisie invalide, et "75001" reste un texte ASCII valide
  // pour hasValidChars. On ne modifie pas `hasValidChars` elle-même : elle
  // est aussi appelée depuis Quotes/Retractions, la durcir dépasse le
  // périmètre de cette MR (candidat à un ticket séparé).
  for (const [key, value] of Object.entries(shippingAddressFields)) {
    if (value && !hasValidChars(String(value))) {
      const message = 'Le champ contient des caractères non valides';
      if (addressErrorKeys.includes(key)) {
        if (!newErrors.shippingAddress[key]) {
          newErrors.shippingAddress[key] = message;
        }
      } else if (!newErrors[key]) {
        newErrors[key] = message;
      }
      isValid = false;
    }
  }

  for (const [key, value] of Object.entries(billingAddressFields)) {
    if (value && !hasValidChars(String(value))) {
      const message = 'Le champ contient des caractères non valides';
      if (addressErrorKeys.includes(key)) {
        if (!newErrors.billingAddress[key]) {
          newErrors.billingAddress[key] = message;
        }
      } else if (!newErrors[key]) {
        newErrors[key] = message;
      }
      isValid = false;
    }
  }
  if (isErrorRequired) {
    setFormError(newErrors);
  }
  return isValid;
};
